// A2F-764: Section 3 body — formatting sub-tabs (simple config).
//
// "Simple config" means: schema fields stay 1:1 with what v1 stores;
// the UI consolidates per-level and rarely-changed knobs into a
// single control whenever it doesn't lose user intent.
//
// Per-tab simplifications vs the v1 FieldCard layout:
//
//   Headings
//     v1: H1, H2, H3 cards each with font/size/bold/color editors
//          + a Numbering display-only card.
//     v2: ONE heading color (writes colors.{1,2,3}); ONE heading font
//         (already 1 schema field); H1 size + H2 size steppers (H3
//         size sticks at v1 default); Bold toggles dropped; Numbering
//         stays display-only (schema doesn't support changing it).
//
//   Body
//     v1: Font, Size, Line spacing, Space after — 2 cards.
//     v2: Same fields, 4 dedicated rows. Line spacing becomes named
//         segments (Tight / Comfortable / Relaxed / Double) so the
//         user picks intent, not a magic number.
//
//   Captions
//     v1: Figure caption card + Table caption card + Position display.
//     v2: ONE caption font / size / color / italic — writes to BOTH
//         figureCaption.* and tableCaption.* on every change. Schema
//         keeps the two slots distinct so v1 users with split styles
//         aren't lost; v2 simple just unifies them.
//
//   Lists
//     v1: Bullet style + Indent — both display-only.
//     v2: Same. Production ribbon retired the Lists group (A2F-610);
//         indent is exposed via the Indentation Menu instead.
//
// Schema-preservation contract: every <ColorSwatch> / <Segmented> /
// <Stepper> call hits onEditField with the same dotted-key value
// FieldCard would have written. Saved profiles round-trip identically.

import { useCallback } from 'react'
import type {
  FormattingFields,
  FormattingValue,
} from '../../data/onboard-v2-fields'
import { BASE_FORMATTING } from '../../data/onboard-v2-fields'
import type { FormattingSubTab } from './useFocusedSection'
import {
  ColorSwatch,
  TEXT_COLOR_PALETTE,
} from './ColorSwatch'
import { Segmented } from './Segmented'
import { Stepper } from './Stepper'
import { Toggle } from '../onboard/v2/Toggle'

// ── Curated option lists ─────────────────────────────────────────────

const HEADING_FONT_OPTIONS = [
  { value: 'Calibri Light', label: 'Calibri Light' },
  { value: 'Cambria', label: 'Cambria' },
  { value: 'Public Sans', label: 'Public Sans' },
  { value: 'Arial', label: 'Arial' },
] as const

const BODY_FONT_OPTIONS = [
  { value: 'Calibri', label: 'Calibri' },
  { value: 'Cambria', label: 'Cambria' },
  { value: 'Public Sans', label: 'Public Sans' },
  { value: 'Times New Roman', label: 'Times' },
] as const

const CAPTION_FONT_OPTIONS = BODY_FONT_OPTIONS

// Named line-spacing options — the underlying schema is a number, but
// users think in intent ("comfortable") not multipliers (1.4).
const LINE_SPACING_OPTIONS = [
  { value: 1.15, label: 'Tight' },
  { value: 1.4, label: 'Comfortable' },
  { value: 1.6, label: 'Relaxed' },
  { value: 2, label: 'Double' },
] as const

// ── Edited check ─────────────────────────────────────────────────────

const ALL_FORMATTING_KEYS = Object.keys(BASE_FORMATTING)

export function isAnyFormattingChanged(state: FormattingFields): boolean {
  for (const k of ALL_FORMATTING_KEYS) {
    if (k in state && state[k] !== BASE_FORMATTING[k]) return true
  }
  return false
}

// ── Section ──────────────────────────────────────────────────────────

interface SectionFormattingProps {
  state: FormattingFields
  subTab: FormattingSubTab
  onSubTabChange: (next: FormattingSubTab) => void
  onEditField: (key: string, value: FormattingValue) => void
}

// Sub-tab id 'lists' is kept for state-shape stability with v1
// (FormattingSubTab type, FIELD_GROUPS group id) — only the user-facing
// label changed to 'Indentation' so it matches the ribbon button.
const SUB_TABS: { id: FormattingSubTab; label: string; hint: string }[] = [
  { id: 'headings', label: 'Headings', hint: 'H1 / H2 / H3 + numbering' },
  { id: 'body', label: 'Body text', hint: 'Font · size · line spacing' },
  { id: 'captions', label: 'Captions', hint: 'Figure & table caption style' },
  { id: 'lists', label: 'Indentation', hint: 'Bullets · indent' },
]

export function SectionFormatting({
  state,
  subTab,
  onSubTabChange,
  onEditField,
}: SectionFormattingProps) {
  // Multi-key writer for consolidated controls. React 18 batches the
  // setState calls inside onEditField automatically, so this lands as
  // a single render — no need for a custom batch path.
  const editMany = useCallback(
    (entries: Array<[string, FormattingValue]>) => {
      for (const [k, v] of entries) onEditField(k, v)
    },
    [onEditField],
  )

  return (
    <div>
      {/* Sub-tabs — bigger title+hint cards, breathing room between */}
      <div className="flex flex-wrap gap-2 mb-6">
        {SUB_TABS.map((t) => {
          const isOn = subTab === t.id
          const tabEdited = isTabEdited(t.id, state)
          return (
            <button
              key={t.id}
              type="button"
              // stopPropagation prevents the click from bubbling to
              // SectionCard's body onClick — without this, the bubbled
              // handler fires onFocus(id) with a stale formattingSubTab
              // closure value (because handleSubTabChange's setState
              // hasn't flushed yet), which silently rings the previous
              // tab's ribbon button. Manifests as Captions/Lists
              // appearing swapped after a click.
              onClick={(e) => {
                e.stopPropagation()
                onSubTabChange(t.id)
              }}
              className={`text-left rounded-lg px-3.5 py-2.5 leading-tight transition-colors border ${
                isOn
                  ? 'bg-brand-orange/5 border-brand-orange text-brand-orange'
                  : 'bg-white border-slate-200 text-brand-charcoal hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-[13px] font-semibold">{t.label}</span>
                {tabEdited && !isOn ? (
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand-orange" />
                ) : null}
              </div>
              <div
                className={`text-[10px] mt-0.5 ${
                  isOn ? 'text-brand-orange/80' : 'text-slate-500'
                }`}
              >
                {t.hint}
              </div>
            </button>
          )
        })}
      </div>

      {/* Active tab body */}
      {subTab === 'headings' && (
        <HeadingsBody state={state} onEditField={onEditField} editMany={editMany} />
      )}
      {subTab === 'body' && (
        <BodyTextBody state={state} onEditField={onEditField} />
      )}
      {subTab === 'captions' && (
        <CaptionsBody state={state} editMany={editMany} />
      )}
      {subTab === 'lists' && <ListsBody />}
    </div>
  )
}

// Per-tab edited status — drives the small dot on inactive sub-tabs.
function isTabEdited(tab: FormattingSubTab, state: FormattingFields): boolean {
  const keys = TAB_KEYS[tab]
  for (const k of keys) {
    if (k in state && state[k] !== BASE_FORMATTING[k]) return true
  }
  return false
}

const TAB_KEYS: Record<FormattingSubTab, readonly string[]> = {
  headings: [
    'headings.font',
    'headings.sizes.1',
    'headings.sizes.2',
    'headings.sizes.3',
    'headings.bold.1',
    'headings.bold.2',
    'headings.bold.3',
    'headings.colors.1',
    'headings.colors.2',
    'headings.colors.3',
  ],
  body: ['body.font', 'body.size', 'body.lineSpacing', 'body.spaceAfter'],
  captions: [
    'figureCaption.font',
    'figureCaption.size',
    'figureCaption.italic',
    'figureCaption.color',
    'tableCaption.font',
    'tableCaption.size',
    'tableCaption.italic',
    'tableCaption.color',
  ],
  lists: [], // display-only
}

// ── Tab bodies ───────────────────────────────────────────────────────

function LabelRow({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">
        {label}
        {hint ? (
          <span className="ml-2 normal-case tracking-normal font-normal text-slate-400">
            {hint}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function DisplayPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] text-slate-500">
      {children}
    </span>
  )
}

// Headings ──────────────────────────────────────────

interface HeadingsBodyProps {
  state: FormattingFields
  onEditField: (key: string, value: FormattingValue) => void
  editMany: (entries: Array<[string, FormattingValue]>) => void
}

function HeadingsBody({ state, onEditField, editMany }: HeadingsBodyProps) {
  // The single Heading color swatch reads colors.1 as the source of
  // truth — if a v1 user has divergent .1/.2/.3, we surface .1 in
  // the picker. Picking a new color writes the same hex to all 3,
  // unifying them on first edit.
  const headingColor = String(state['headings.colors.1'])
  const headingFont = String(state['headings.font'])
  const h1Size = Number(state['headings.sizes.1'])
  const h2Size = Number(state['headings.sizes.2'])

  return (
    <div className="space-y-6">
      {/* Font row at the top — keeps the same vertical position as
          Body and Captions tabs, so the user's eye lands in the same
          place every time they switch tabs. */}
      <LabelRow label="Heading font">
        <Segmented
          value={headingFont}
          options={HEADING_FONT_OPTIONS}
          ariaLabel="Heading font"
          onChange={(v) => onEditField('headings.font', v)}
        />
      </LabelRow>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LabelRow label="Heading color" hint="applies to H1–H3">
          <ColorSwatch
            value={headingColor}
            palette={TEXT_COLOR_PALETTE}
            ariaLabelPrefix="Heading color"
            onChange={(hex) =>
              editMany([
                ['headings.colors.1', hex],
                ['headings.colors.2', hex],
                ['headings.colors.3', hex],
              ])
            }
          />
        </LabelRow>

        <LabelRow label="Numbering">
          <DisplayPill>eCTD (3.2.P.5.4)</DisplayPill>
        </LabelRow>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LabelRow label="H1 size">
          <Stepper
            value={h1Size}
            min={12}
            max={32}
            step={0.5}
            ariaLabel="H1 size"
            onChange={(v) => onEditField('headings.sizes.1', v)}
          />
        </LabelRow>
        <LabelRow label="H2 size">
          <Stepper
            value={h2Size}
            min={10}
            max={24}
            step={0.5}
            ariaLabel="H2 size"
            onChange={(v) => onEditField('headings.sizes.2', v)}
          />
        </LabelRow>
      </div>
    </div>
  )
}

// Body text ──────────────────────────────────────────

interface BodyTextBodyProps {
  state: FormattingFields
  onEditField: (key: string, value: FormattingValue) => void
}

function BodyTextBody({ state, onEditField }: BodyTextBodyProps) {
  const bodyFont = String(state['body.font'])
  const bodySize = Number(state['body.size'])
  const lineSpacing = Number(state['body.lineSpacing'])
  const spaceAfter = Number(state['body.spaceAfter'])
  return (
    <div className="space-y-6">
      <LabelRow label="Body font">
        <Segmented
          value={bodyFont}
          options={BODY_FONT_OPTIONS}
          ariaLabel="Body font"
          onChange={(v) => onEditField('body.font', v)}
        />
      </LabelRow>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LabelRow label="Body size">
          <Stepper
            value={bodySize}
            min={8}
            max={18}
            step={0.5}
            ariaLabel="Body size"
            onChange={(v) => onEditField('body.size', v)}
          />
        </LabelRow>
        <LabelRow label="Line spacing">
          <Segmented
            value={lineSpacing}
            options={LINE_SPACING_OPTIONS}
            ariaLabel="Line spacing"
            onChange={(v) => onEditField('body.lineSpacing', v)}
          />
        </LabelRow>
      </div>

      <LabelRow label="Space after paragraph">
        <Stepper
          value={spaceAfter}
          min={0}
          max={24}
          step={1}
          ariaLabel="Space after paragraph"
          onChange={(v) => onEditField('body.spaceAfter', v)}
        />
      </LabelRow>
    </div>
  )
}

// Captions ──────────────────────────────────────────

interface CaptionsBodyProps {
  state: FormattingFields
  editMany: (entries: Array<[string, FormattingValue]>) => void
}

function CaptionsBody({ state, editMany }: CaptionsBodyProps) {
  // Same source-of-truth pattern as headings: read figureCaption.*
  // as the picker's current value. Edits propagate to BOTH halves.
  const font = String(state['figureCaption.font'])
  const size = Number(state['figureCaption.size'])
  const italic = Boolean(state['figureCaption.italic'])
  const color = String(state['figureCaption.color'])

  return (
    <div className="space-y-6">
      <LabelRow label="Caption font" hint="applies to figure & table captions">
        <Segmented
          value={font}
          options={CAPTION_FONT_OPTIONS}
          ariaLabel="Caption font"
          onChange={(v) =>
            editMany([
              ['figureCaption.font', v],
              ['tableCaption.font', v],
            ])
          }
        />
      </LabelRow>

      {/* Color on the left, size on the right — same column position
          as Headings tab so color picker lands in the same spot below
          the font row when the user switches between tabs. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LabelRow label="Caption color">
          <ColorSwatch
            value={color}
            palette={TEXT_COLOR_PALETTE}
            ariaLabelPrefix="Caption color"
            onChange={(hex) =>
              editMany([
                ['figureCaption.color', hex],
                ['tableCaption.color', hex],
              ])
            }
          />
        </LabelRow>
        <LabelRow label="Caption size">
          <Stepper
            value={size}
            min={6}
            max={14}
            step={0.5}
            ariaLabel="Caption size"
            onChange={(v) =>
              editMany([
                ['figureCaption.size', v],
                ['tableCaption.size', v],
              ])
            }
          />
        </LabelRow>
      </div>

      <LabelRow label="Italic">
        <div className="flex items-center gap-2">
          <Toggle
            on={italic}
            accent="orange"
            ariaLabel="Italic captions"
            onClick={() =>
              editMany([
                ['figureCaption.italic', !italic],
                ['tableCaption.italic', !italic],
              ])
            }
          />
          <span className="text-[12px] text-slate-600">
            {italic ? 'On' : 'Off'}
          </span>
        </div>
      </LabelRow>
    </div>
  )
}

// Lists (display-only) ───────────────────────────────

function ListsBody() {
  return (
    <div className="space-y-6">
      <LabelRow label="Bullet style">
        <DisplayPill>Filled circle</DisplayPill>
      </LabelRow>
      <LabelRow label="Indent">
        <DisplayPill>0.5″ per level</DisplayPill>
      </LabelRow>
      <p className="text-[11px] text-slate-400 leading-relaxed">
        Bullet style and base indent are inherited from Word. Adjust
        per-document via the{' '}
        <span className="font-medium text-slate-500">Indentation</span>{' '}
        button on the Chrona ribbon.
      </p>
    </div>
  )
}
